import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import {
  ListGroup,
  ListGroupItem,
  Card as CardBase,
  CardBody,
  CardTitle,
  CardText,
  CardSubtitle,
  Row,
  Col
} from 'reactstrap'
import { Scrollbars } from 'react-custom-scrollbars'
import ApiFetcher from 'lib/api_fetcher'
import { useTranslation } from 'react-i18next'
import ComponentSpinner from 'ui/component_spinner'
import useDebounce from 'utils/hooks/use_debounce'
import { currency } from 'i18n/config'
import { scrollIntoView } from 'scroll-js'
import { isMobile } from 'react-device-detect'
import {tourTypeColors} from 'containers/list/classes'
import cn from 'classnames'

const Card = styled(CardBase).attrs()`
  transition: 0.15s;
  border-color: var(--bs-gray-300);
  overflow: hidden;

  .product-title {
    color: var(--bs-dark);
  }

  &:hover {
    border-color: var(--bs-primary);
    box-shadow: var(--bs-box-shadow) !important;
    cursor: pointer;

    img {
      /* transform: scale(1.05); */
    }

    .product-title {
      color: var(--bs-primary);
    }
  }
`

const SearchResultsWrapper = styled.div.attrs(() => ({
  className: 'text-dark rounded shadow-lg p-4 bg-white rounded-3',
}))`
  position: absolute;
  width: 100%;
  top: ${(props) => props.wide ? '75px' : '50px'};
  right: 0;
  z-index: 99999;
  text-shadow: none;
  text-align: left;

  @media (min-width: 768px) {
    width: ${(props) => props.wide ? '100%' : '200%'} !important;
  }
`

const SearchBoxWrapper = styled.div`
  position: relative;
  transition: all 0.25s;
  width: 100%;
  height: 32px;
  background: var(--bs-body-bg);
  display: flex;
  align-items: center;
  padding: 0;
  margin: 2px;

  &:hover {
    background: rgba(255, 255, 255, 1);

    .input-button::after {
      content: '${(props) => props.search_text}';
      margin-left: 3px;
    }

    .input-button {
      width: 90px !important;
    }
  }

  input {
    background: transparent;
    flex: 1;
    border: 0;
    outline: none;
    padding: 3px 0px 3px 20px;
    font-size: 1em;
    color: #000;
  }

  .input-button {
    transition: all 1.2s;
    transition-delay: -0.28s;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1em;
    border: 0;
    border-radius: 5px;
    width: 33px;
    height: 27px;
    margin-right: 2px;
    cursor: pointer;
  }

  @media (min-width: 768px) {
    width: 16.5em !important;
  }

  @media (min-width: 992px) {
    width: 21.3em !important;
  }
`

const FeaturedPrice = styled.span.attrs()`
  cursor: pointer;
  transition: .3s ease;
  border: 0px;
  border-radius: 0.15em;
  font-size: 0.9em;

  strong {
    font-size: 1.3em;
  }

  &:hover {
    color: var(--bs-primary);
  }
`

const SearchBox = ({ productLink, user, wide = false }) => {
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('a')
  const [response, setResponse] = useState(null)
  const [showBox, setShowBox] = useState(false)
  const { i18n } = useTranslation()
  const sale_channel = user.user_data.user.sale_channel
  const debouncedSearchTerm = useDebounce(() => searchTerm, 500)
  const node = useRef()

  useEffect(() => {
    if (searchTerm && searchTerm.length >= 3) {
      setLoading(true)
      new ApiFetcher({
        key: 'search',
        method: 'GET',
        endpoint: '/search',
        query: { term: searchTerm },
      })
        .call()
        .then((res) => {
          setResponse(res)
        })
        .finally(() => setLoading(false))
    }
  }, [debouncedSearchTerm])

  const handleClick = (e) => {
    if (!node.current || !node.current.contains(e.target)) {
      setShowBox(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const ProductCard = (p) => {
    const {
      slug,
      title,
      cover_image,
      state_name,
      city_name,
      supplier_name,
      tour_type,
      tour_type_label,
      status,
      featured_price,
      featured_price_label
    } = p
    const url = productLink.replace(/ *\:[^)]*\: */g, slug)

    return (
      <Card className="mb-3 me-3 shadow-sm rounded-3" onClick={() => window.location.replace(url)}>
        <Row className="g-0">
          {/* Imagen a la izquierda */}
          <Col xs="4" md="3">
            <img
              src={cover_image}
              alt={title}
              className="img-fluid rounded-start h-100 object-fit-cover"
            />
          </Col>

          {/* Contenido a la derecha */}
          <Col xs="8" md="9" className="d-flex flex-column justify-content-between">
            <CardBody className="pb-0">
              <CardTitle tag="a" href={url} className='product-title fw-semibold h6' style={{fontSize: '1.1em'}}>{title}</CardTitle>
              <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between pt-3">
                {sale_channel === 'b2b' ? (
                  city_name && supplier_name && <div style={{fontSize: '0.9em'}}>{city_name} ● {supplier_name}</div>
                ) : (
                  state_name && city_name && (
                    <div style={{fontSize: '0.7em'}}>
                      {state_name === city_name ? state_name : `${state_name} ● ${city_name}`}
                    </div>
                  )
                )}
                {tour_type_label && (
                  <div>
                    <small className={cn("rounded px-2 py-1 fw-semibold", tourTypeColors(tour_type))} style={{fontSize: '0.7em'}}>
                      {tour_type_label}
                    </small>
                  </div>
                )}
              </div>
            </CardBody>

            {/* Footer alineado a la derecha */}
            <div className="card-footer bg-transparent border-0 text-end px-3">
              <hr className='my-2' style={{color: 'var(--bs-gray-500)'}}/>

              {featured_price_label ? (
                <FeaturedPrice onClick={() => status == 'active' ? (window.location = url) : null}>
                  {i18n.t('product_card.from')} <strong className="h3 fw-bold">{featured_price_label}</strong>
                </FeaturedPrice>
              ) : (
                <strong className="h3" style={{fontSize: '1.22em'}}>
                  {i18n.t('product_card.no_feature_price_label')}
                </strong>
              )}
            </div>
          </Col>
        </Row>
      </Card>
    )
  }

  const SearchResults = () => {
    const products = response?.products || []
    const otherProducts = response?.other_destinations || []
    const productsCount = products.length

    const title = loading
      ? i18n.t('search_results.loading')
      : searchTerm.length < 3
      ? i18n.t('search_results.write_a_term')
      : otherProducts.length
      ? i18n.t('search_results.results_count_with_destination', {
          count: productsCount,
          destination: response.destination?.name,
        })
      : i18n.t('search_results.results_count', { count: productsCount })

    return (
      <SearchResultsWrapper wide={wide}>
        <div className='h5 fw-semibold mb-3 py-0'>
          {title}
        </div>
        <div>
          <Scrollbars autoHeight autoHeightMin={120} autoHeightMax={380}>
            {loading && <ComponentSpinner />}
            {!loading && products.length > 0 ? (
              products.map((p) => <ProductCard key={p.id} {...p} />)
            ) : (
              <ListGroupItem className="py-4 text-center text-muted">
                {i18n.t('search_results.no_results')}
              </ListGroupItem>
            )}
          </Scrollbars>

          {otherProducts.length > 0 && (
            <>
              <hr className="mt-4 text-dark" style={{borderTop: '3px solid'}}/>
              <div className='h5 fw-semibold mt-3 mb-3'>
                {i18n.t('search_results.other_destinations_title', {
                  count: otherProducts.length,
                })}
              </div>

              <Scrollbars autoHeight autoHeightMin={120} autoHeightMax={300}>
                {otherProducts.map((p) => <ProductCard key={p.id} {...p} />)}
              </Scrollbars>
            </>
          )}
        </div>
      </SearchResultsWrapper>
    )
  }

  if (wide) {
    return (
      <div
        search_text={i18n.t('format:actions.search')}
        ref={node}
        className="search-box rounded-5 shadow-lg py-2"
        style={{position: 'relative'}}
        onClick={() => {
          console.log('CLICKK')
          if (isMobile) {
            const el = document.body.getElementsByClassName('search-box')[0]
            console.log(el)
            if (el) scrollIntoView(el, document.body, { behavior: 'smooth' })
          }
          setShowBox(true)
        }}
      >
        {showBox && <SearchResults />}
        <input
          onChange={(e) => setSearchTerm(e.target.value)}
          type="text"
          className="form-control"
          placeholder={i18n.t('search_results.wide_placeholder')}
          aria-label={i18n.t('format:actions.search')}
          style={{
            height: '60px',
            borderRadius: '50px',
            padding: '0 130px 0 45px',
            fontSize: '1.1rem',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        />
        <button
          onClick={(e) => {
            e.stopPropagation()
            setShowBox(!showBox)
          }}
          className="btn btn-primary text-white"
          type="button"
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            borderRadius: '50px',
            padding: '0 25px',
            height: '46px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            whiteSpace: 'nowrap'
          }}
        >
          <i className="fa fa-search" />
          {!isMobile &&
            <span>{i18n.t('format:actions.search')}</span>
          }
        </button>
      </div>
    )
  } else {
    return (
      <SearchBoxWrapper
        search_text={i18n.t('format:actions.search')}
        ref={node}
        className="search-box rounded-5 shadow-lg"
        onClick={() => {
          if (isMobile) {
            const el = document.body.getElementsByClassName('search-box-content')[0]
            if (el) scrollIntoView(el, document.body, { behavior: 'smooth' })
          }
          setShowBox(true)
        }}
      >
        {showBox && <SearchResults />}
        <input
          type="text"
          placeholder={i18n.t('search_box.input_placeholder')}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div
          className="input-button search-box-button rounded-5"
          onClick={(e) => {
            e.stopPropagation()
            setShowBox(!showBox)
          }}
        >
          <i className="fa fa-search search-box-button-icon" />
        </div>
      </SearchBoxWrapper>
    )
  }
}

export default SearchBox
